// @ts-nocheck TODO: Remove this comment after fixing the typing
import { css } from '@emotion/react';
import styled from '@emotion/styled';

// TODO: Deprecate these in favor of proper design tokens
export const color = {
  // Palette
  primary: '#FF4785', // Coral
  secondary: 'hsl(212 100 46)', // Ocean
  tertiary: 'hsl(212 10 90)', // Light grey

  orange: '#FC521F',
  gold: 'hsl(41 100 50)',
  green: 'hsl(101 52 49)',
  seafoam: 'hsl(179 65 53)',
  purple: 'hsl(271 59 42)',
  ultraviolet: 'hsl(258 94 26)',
  red: 'hsl(16 100 50)',

  // Calm
  bluelight: 'hsl(212 100 95)',
  bluelighter: 'hsl(212 100 98)',

  // Monochrome
  lightest: '#FFFFFF',
  lighter: 'hsl(212 45 98)',
  light: 'hsl(212 31 95)',
  mediumlight: 'hsl(212 52 95)',
  medium: 'hsl(212 49 90)',
  mediumdark: 'hsl(212 10 50)',
  dark: 'hsl(212 10 40)',
  darker: 'hsl(212 10 30)',
  darkest: 'hsl(212 10 20)',
  tr5: 'hsl(0 0 0 / 0.05)',
  tr10: 'hsl(0 0 0 / 0.1)',

  border: 'hsl(212 50 30 / 0.15)',

  // Status text
  positive: 'hsl(101 52 32)', // Evergreen
  negative: 'hsl(16 100 38)', // Crimson
  warning: 'hsl(31 66 35)', // Burnt sienna
  selected: 'hsl(212 100 38)', // Navy
  calm: 'hsl(212 100 38)', // Navy
};

export const border = {
  app: 'hsl(212 50 30 / 0.15)',
  positive: 'hsl(101 55 79)',
  warning: 'hsl(41 80 76)',
  negative: 'hsl(16 100 84)',
  calm: 'hsl(212 80 84)',
};

// Global style variables
export const background = {
  app: 'hsl(212 55 98)',
  appInverse: 'hsl(212 10 40)',
  bar: color.lightest,
  content: color.lightest,
  preview: color.lightest,
  hoverable: `hsl(from ${color.secondary} h s l / 0.1)`, // hover state for items in a list
  gridCellSize: 10,

  // Notification, error, and warning backgrounds
  positive: 'hsl(101 100 96)',
  warning: 'hsl(41 100 96)',
  negative: 'hsl(16 100 96)',
  critical: 'hsl(16 100 41)',
  calmBlue: 'hsl(212 100 96)',
};

// TODO: Update with better typography tokens
export const typography = {
  fonts: {
    base: [
      "'Nunito Sans'",
      '-apple-system',
      "'.SFNSText-Regular'",
      "'San Francisco'",
      'BlinkMacSystemFont',
      "'Segoe UI'",
      "'Helvetica Neue'",
      'Helvetica',
      'Arial',
      'sans-serif',
    ].join(', '),
    mono: [
      'ui-monospace',
      'Menlo',
      'Monaco',
      "'Roboto Mono'",
      "'Oxygen Mono'",
      "'Ubuntu Monospace'",
      "'Source Code Pro'",
      "'Droid Sans Mono'",
      "'Courier New'",
      'monospace',
    ].join(', '),
  },
  type: {
    primary:
      'var(--nunito-sans, "Nunito Sans"), "Nunito Sans", "Helvetica Neue", Helvetica, Arial, sans-serif',
    code: '"SFMono-Regular", Consolas, "Liberation Mono", Menlo, Courier, monospace',
  },
  weight: {
    regular: '400',
    bold: '700',
    extrabold: '800',
  },
  size: {
    s1: 12,
    s2: 14,
    s3: 16,
    m1: 20,
    m2: 24,
    m3: 28,
    l1: 32,
    l2: 40,
    l3: 48,
    code: 90,
  },
} as const;

// TODO: Replace with design tokens or simply remove
export const spacing = {
  padding: {
    small: 10,
    medium: 20,
    large: 30,
  },
  borderRadius: {
    extraSmall: 3,
    small: 5,
    default: 10,
  },
};

export const breakpoint = 600;
export const xlargebreakpoint = 1600;
export const pageMargin = 5.55555;

export const pageMargins = css`
  padding: 0 ${spacing.padding.medium}px;
  @media (min-width: ${breakpoint * 1}px) {
    margin: 0 ${pageMargin * 1}%;
  }
  @media (min-width: ${breakpoint * 2}px) {
    margin: 0 ${pageMargin * 2}%;
  }
  @media (min-width: ${breakpoint * 3}px) {
    margin: 0 ${pageMargin * 3}%;
  }
  @media (min-width: ${breakpoint * 4}px) {
    margin: 0 ${pageMargin * 4}%;
  }
`;

export const hoverEffect = css`
  border: 1px solid ${color.border};
  border-radius: ${spacing.borderRadius.small}px;
  transition:
    background 150ms ease-out,
    border 150ms ease-out,
    transform 150ms ease-out;

  &:hover,
  &.__hover {
    border-color: hsl(from ${color.secondary} h s l / 0.5);
    transform: translate3d(0, -3px, 0);
    box-shadow: rgba(0, 0, 0, 0.08) 0 3px 10px 0;
  }

  &:active,
  &.__active {
    border-color: transparent;
    transform: translate3d(0, 0, 0);
  }
`;

// Typography styles
export const headers = {
  hero1: css({
    fontSize: typography.size.l3,
    fontWeight: typography.weight.bold,
  }),
  hero2: css({
    fontSize: typography.size.l2,
    fontWeight: typography.weight.bold,
  }),
  h1: css({
    fontSize: typography.size.l1,
    fontWeight: typography.weight.bold,
  }),
  h2: css({
    fontSize: typography.size.m3,
    fontWeight: typography.weight.bold,
  }),
  h3: css({
    fontSize: typography.size.m2,
    fontWeight: typography.weight.bold,
  }),
  h4: css({
    fontSize: typography.size.m1,
    fontWeight: typography.weight.bold,
  }),
  h5: css({
    fontSize: typography.size.s3,
    fontWeight: typography.weight.bold,
  }),
  h6: css({
    fontSize: typography.size.s2,
    fontWeight: typography.weight.bold,
  }),
};

export const subheading = {
  regular: css({
    fontSize: 13,
    fontWeight: typography.weight.extrabold,
    lineHeight: '18px',
    letterSpacing: '0.38em',
  }),
  small: css({
    fontSize: 11,
    fontWeight: typography.weight.extrabold,
    lineHeight: '16px',
    letterSpacing: '0.38em',
  }),
};

export const text = {
  storybookMediumBold: css({
    fontSize: 13,
    fontWeight: typography.weight.bold,
    lineHeight: '18px',
  }),
  storybookMedium: css({
    fontSize: 13,
    fontWeight: typography.weight.regular,
    lineHeight: '18px',
  }),
  largeBold: css({
    fontSize: typography.size.s3,
    fontWeight: typography.weight.bold,
    lineHeight: '24px',
  }),
  small: css({
    fontSize: typography.size.s1,
    fontWeight: typography.weight.regular,
    lineHeight: '18px',
  }),
  regularBold: css({
    fontSize: typography.size.s2,
    fontWeight: typography.weight.bold,
    lineHeight: '20px',
  }),
  smallBold: css({
    fontSize: typography.size.s1,
    fontWeight: typography.weight.bold,
    lineHeight: '18px',
  }),
  large: css({
    fontSize: typography.size.s3,
    fontWeight: typography.weight.regular,
    lineHeight: '24px',
  }),
  regular: css({
    fontSize: typography.size.s2,
    fontWeight: typography.weight.regular,
    lineHeight: '20px',
  }),
};

export const code = {
  regular: css({
    fontFamily: typography.type.code,
    fontSize: typography.size.s2,
    fontWeight: typography.weight.regular,
    lineHeight: '17px',
  }),

  small: css({
    fontFamily: typography.type.code,
    fontSize: typography.size.s1,
    fontWeight: typography.weight.regular,
    lineHeight: '14px',
  }),
};

export const paper = css`
  background: ${color.lightest};
  border-radius: ${spacing.borderRadius.small}px;
  box-shadow: ${color.tr5} 0 1px 3px 0;
`;

export const activeElement = css`
  box-shadow: ${color.secondary} 0 0 5px 0;
`;

export const activeElementHover = css`
  box-shadow:
    ${color.secondary} 0 0 5px 0,
    ${color.tr10} 0 3px 10px 0;
`;

// TODO: Deprecate these colors in favor of the ones in the color const
export const lightBlue = '#E3F3FF';
export const backgroundNote = '#f3faff';
export const backgroundWarning = '#fdf5d3';

export const zIndex = {
  tooltip: 2147483647,
  popover: 2147483646,
  canvasLabel: 10,
  testBrowserHeading: 10,
  reviewTabBar: 10,
  pinnedCommentThreadTooltip: 9,
  canvasButtons: 9,
  canvasIframeErrorMessage: 8,
  canvasIgnoredRegionsOverlay: 7,
  magnifier: 6,
  pinHover: 6,
  pinNew: 5,
  pin: 4,
  pinBackground: 3,
  pinCoordinatePlane: 2,
  accessibilityOverlay: 3,
};

export const Flex = styled.div`
  display: flex;
`;

export const tooltipCSS = css`
  box-shadow:
    0px 0px 15px rgba(0, 0, 0, 0.05),
    0px 1px 2px rgba(0, 0, 0, 0.1);
  border-radius: 8px;
`;

/* TABLE STYLES */
export const cellBlockPadding = css`
  padding-block: 10px;
`;

export const innerCellPadding = css`
  padding-inline: 0.75em;
`;

export const lastColumnPadding = css`
  padding-inline-end: ${spacing.padding.medium}px;
  @media (min-width: ${breakpoint * 1.333}px) {
    padding-inline-end: ${spacing.padding.large}px;
  }
`;

export const syntax = {
  blue: `hsl(from ${color.secondary} h 100 40)`,
  green: `hsl(from ${color.green} h 89 26)`,
  orange: `hsl(from ${color.orange} h 67 40)`,
  purple: `hsl(from ${color.purple} h 90 43)`,
  red: `hsl(from ${color.red} h 90 40)`,
  seafoam: `hsl(from ${color.seafoam} h 39 44)`,
};
